/**
 * PreviewPane Component
 *
 * Live preview of markdown content with variable interpolation.
 * Uses a hybrid approach: Markdown for text, direct HTML for special blocks.
 */
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useEditorStore } from '../../stores/editorStore';
import { interpolateVariables, OfferContext, formatCurrency } from '../../types/editor';

interface PreviewPaneProps {
  content?: string;
  context?: OfferContext;
  className?: string;
  showItemsTable?: boolean;
  items?: Array<{
    descrizione: string;
    quantita: number;
    prezzo_unitario: number;
    sconto_percentuale?: number;
    importo_totale: number;
  }>;
}

// Placeholder markers for special blocks
const ITEMS_TABLE_MARKER = '___ITEMS_TABLE___';
const TOTALS_MARKER = '___TOTALS___';
const SIGNATURE_MARKER = '___SIGNATURE___';
const CLIENTE_INFO_MARKER = '___CLIENTE_INFO___';

export default function PreviewPane({
  content: propContent,
  context: propContext,
  className = '',
  showItemsTable = true,
  items = [],
}: PreviewPaneProps) {
  // Use store values if props not provided - use selectors to prevent unnecessary re-renders
  const storeContent = useEditorStore((state) => state.content);
  const storeContext = useEditorStore((state) => state.offerContext);

  const content = propContent ?? storeContent;
  const context = propContext ?? storeContext;

  // Process content and split into parts
  const parts = useMemo(() => {
    let processedContent = content;

    if (context) {
      processedContent = interpolateVariables(content, context);
    }

    // Replace special blocks with markers
    processedContent = processedContent.replace(/\{\{items_table\}\}/g, ITEMS_TABLE_MARKER);
    processedContent = processedContent.replace(/\{\{totals\}\}/g, TOTALS_MARKER);
    processedContent = processedContent.replace(/\{\{signature_block\}\}/g, SIGNATURE_MARKER);
    processedContent = processedContent.replace(/\{\{cliente_info\}\}/g, CLIENTE_INFO_MARKER);

    // Split content by markers
    const allMarkers = [ITEMS_TABLE_MARKER, TOTALS_MARKER, SIGNATURE_MARKER, CLIENTE_INFO_MARKER];
    const regex = new RegExp(`(${allMarkers.join('|')})`, 'g');
    const splitParts = processedContent.split(regex);

    return splitParts.map((part, index) => {
      if (part === ITEMS_TABLE_MARKER) {
        return { type: 'items_table' as const, key: `items-${index}` };
      }
      if (part === TOTALS_MARKER) {
        return { type: 'totals' as const, key: `totals-${index}` };
      }
      if (part === SIGNATURE_MARKER) {
        return { type: 'signature' as const, key: `signature-${index}` };
      }
      if (part === CLIENTE_INFO_MARKER) {
        return { type: 'cliente_info' as const, key: `cliente-${index}` };
      }
      return { type: 'markdown' as const, content: part, key: `md-${index}` };
    });
  }, [content, context]);

  return (
    <div className={`prose prose-sm max-w-none p-4 overflow-auto bg-white ${className}`}>
      {parts.map((part) => {
        if (part.type === 'items_table') {
          return showItemsTable && items.length > 0 ? (
            <ItemsTable key={part.key} items={items} />
          ) : null;
        }
        if (part.type === 'totals') {
          return showItemsTable && items.length > 0 ? (
            <TotalsBlock key={part.key} items={items} offerta={context?.offerta} />
          ) : null;
        }
        if (part.type === 'signature') {
          return <SignatureBlock key={part.key} />;
        }
        if (part.type === 'cliente_info') {
          return context?.cliente ? (
            <ClienteInfoBlock key={part.key} cliente={context.cliente} />
          ) : null;
        }
        // Markdown content
        return part.content ? (
          <MarkdownBlock key={part.key} content={part.content} />
        ) : null;
      })}
    </div>
  );
}

// Markdown rendering component
function MarkdownBlock({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children }) => (
          <table className="min-w-full divide-y divide-gray-200 border border-gray-300 my-4">
            {children}
          </table>
        ),
        thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
        tbody: ({ children }) => (
          <tbody className="bg-white divide-y divide-gray-200">{children}</tbody>
        ),
        th: ({ children }) => (
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="px-4 py-2 text-sm text-gray-900">{children}</td>
        ),
        h1: ({ children }) => (
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-6">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-lg font-medium text-gray-700 mb-2 mt-4">{children}</h3>
        ),
        p: ({ children }) => (
          <p className="text-gray-700 mb-3 leading-relaxed">{children}</p>
        ),
        ul: ({ children }) => (
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-1">{children}</ol>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 text-gray-700 italic">
            {children}
          </blockquote>
        ),
        code: ({ children, className }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm text-gray-800">
                {children}
              </code>
            );
          }
          return (
            <code className="block bg-gray-100 p-3 rounded text-sm text-gray-800 overflow-x-auto">
              {children}
            </code>
          );
        },
        strong: ({ children }) => (
          <strong className="font-semibold text-gray-900">{children}</strong>
        ),
        em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
        hr: () => <hr className="my-6 border-t border-gray-300" />,
        a: ({ href, children }) => (
          <a
            href={href}
            className="text-blue-600 hover:text-blue-800 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// Items Table component
interface ItemsTableProps {
  items: PreviewPaneProps['items'];
}

function ItemsTable({ items }: ItemsTableProps) {
  if (!items || items.length === 0) return null;

  return (
    <table className="min-w-full divide-y divide-gray-200 border border-gray-300 my-4">
      <thead className="bg-gray-100">
        <tr>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
            Descrizione
          </th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
            Qtà
          </th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
            Prezzo Unit.
          </th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
            Sconto
          </th>
          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
            Totale
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {items.map((item, index) => (
          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            <td className="px-4 py-2 text-sm text-gray-900">{item.descrizione}</td>
            <td className="px-4 py-2 text-sm text-gray-900 text-right">{item.quantita}</td>
            <td className="px-4 py-2 text-sm text-gray-900 text-right">
              {formatCurrency(item.prezzo_unitario)}
            </td>
            <td className="px-4 py-2 text-sm text-gray-900 text-right">
              {item.sconto_percentuale ? `${item.sconto_percentuale}%` : '-'}
            </td>
            <td className="px-4 py-2 text-sm text-gray-900 text-right font-medium">
              {formatCurrency(item.importo_totale)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Totals Block component
interface TotalsBlockProps {
  items: PreviewPaneProps['items'];
  offerta?: OfferContext['offerta'];
}

function TotalsBlock({ items, offerta }: TotalsBlockProps) {
  const imponibile =
    offerta?.importo_imponibile ?? (items?.reduce((sum, item) => sum + item.importo_totale, 0) || 0);
  const iva = offerta?.importo_iva ?? imponibile * 0.22;
  const totale = offerta?.importo_totale ?? imponibile + iva;

  return (
    <div className="mt-4 flex justify-end">
      <div className="w-64 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Imponibile:</span>
          <span className="font-medium">{formatCurrency(imponibile)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">IVA (22%):</span>
          <span className="font-medium">{formatCurrency(iva)}</span>
        </div>
        <div className="flex justify-between text-lg border-t pt-2">
          <span className="font-semibold">Totale:</span>
          <span className="font-bold text-blue-600">{formatCurrency(totale)}</span>
        </div>
      </div>
    </div>
  );
}

// Signature Block component
function SignatureBlock() {
  return (
    <div className="mt-8 pt-8 border-t border-gray-300">
      <div className="grid grid-cols-2 gap-8">
        <div>
          <p className="text-sm text-gray-600 mb-8">Per accettazione:</p>
          <div className="border-b border-gray-400 w-48 mb-2" />
          <p className="text-xs text-gray-500">Firma e timbro</p>
        </div>
        <div>
          <p className="text-sm text-gray-600 mb-8">Data:</p>
          <div className="border-b border-gray-400 w-32 mb-2" />
        </div>
      </div>
    </div>
  );
}

// Cliente Info Block component
interface ClienteInfoBlockProps {
  cliente: OfferContext['cliente'];
}

function ClienteInfoBlock({ cliente }: ClienteInfoBlockProps) {
  const lines: string[] = [];

  if (cliente.ragione_sociale || cliente.nome) {
    lines.push(cliente.ragione_sociale || cliente.nome || '');
  }
  if (cliente.indirizzo) {
    lines.push(cliente.indirizzo);
  }
  if (cliente.partita_iva) {
    lines.push(`P.IVA: ${cliente.partita_iva}`);
  }
  if (cliente.email) {
    lines.push(`Email: ${cliente.email}`);
  }
  if (cliente.telefono) {
    lines.push(`Tel: ${cliente.telefono}`);
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 my-4">
      <p className="text-sm text-gray-500 mb-2">Destinatario:</p>
      {lines.map((line, index) => (
        <p key={index} className="text-sm text-gray-900">
          {index === 0 ? <strong>{line}</strong> : line}
        </p>
      ))}
    </div>
  );
}

